import { Component, Input, Output, EventEmitter, signal, computed, OnDestroy, OnChanges, SimpleChanges, HostListener, ElementRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { trigger, transition, style, animate } from '@angular/animations';
import { IconComponentV2 } from '../icon-component/icon.component';
import { SidebarService } from '../../../core/services/sidebar.service';

export interface TransactionLogColumn {
  key: string;
  label: string;
  type?: 'text' | 'date' | 'currency' | 'status' | 'badge';
  width?: string;
  searchable?: boolean; // Mark columns that should be searchable
}

export interface TransactionLogFilter {
  key: string;
  label: string;
  active?: boolean;
}

export interface MonthYearOption {
  key: string;
  label: string;
  month: number;
  year: number;
  count: number;
}

export interface TransactionLogsPanelConfig {
  title: string;
  subtitle?: string;
  data: any[];
  columns: TransactionLogColumn[];
  filters?: TransactionLogFilter[];
  accentColor?: 'indigo' | 'emerald' | 'amber' | 'rose';
  searchableColumns?: string[]; // Optional: specify which columns to search
  enableMonthYearFilter?: boolean; // Enable month-year dropdown filter
  dateFieldKey?: string; // Field to use for month-year filtering (default: 'arrangement_date')
}

@Component({
  selector: 'app-transaction-logs-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, IconComponentV2],
  templateUrl: './transaction-logs-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [
    trigger('slidePanel', [
      transition(':enter', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate(
          '400ms cubic-bezier(0.16, 1, 0.3, 1)',
          style({ transform: 'translateX(0)', opacity: 1 })
        ),
      ]),
      transition(':leave', [
        animate(
          '350ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ transform: 'translateX(100%)', opacity: 0 })
        ),
      ]),
    ]),
    trigger('fadeBackdrop', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms ease-out', style({ opacity: 1 })),
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0 })),
      ]),
    ]),
  ],
})
export class TransactionLogsPanelComponent implements OnDestroy, OnChanges {
  private elementRef = inject(ElementRef);
  private sidebarService = inject(SidebarService);

  isSidebarCollapsed = this.sidebarService.isCollapsed;
  
  @Input() open = false;
  @Input() config: TransactionLogsPanelConfig | null = null;
  @Output() closed = new EventEmitter<void>();

  activeFilter = signal<string>('all');
  searchQuery = signal<string>('');
  selectedMonthYear = signal<string>('all');
  monthYearOptions = signal<MonthYearOption[]>([]);
  isMonthDropdownOpen = signal<boolean>(false);
  
  // Use a signal to track config data for reactivity
  private configData = signal<any[]>([]);

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    // Close dropdown when clicking outside
    if (this.isMonthDropdownOpen()) {
      const target = event.target as HTMLElement;
      const dropdownElement = this.elementRef.nativeElement.querySelector('[data-month-dropdown]');
      if (dropdownElement && !dropdownElement.contains(target)) {
        this.isMonthDropdownOpen.set(false);
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When config changes, reset filter, search and update config data signal
    if (changes['config']) {
      this.activeFilter.set('all');
      this.searchQuery.set('');
      this.selectedMonthYear.set('all');
      this.isMonthDropdownOpen.set(false);
      this.configData.set(this.config?.data || []);
      
      // Generate month-year options if enabled
      if (this.config?.enableMonthYearFilter) {
        this.generateMonthYearOptions();
      } else {
        this.monthYearOptions.set([]);
      }
    }
  }

  ngOnDestroy(): void {
    this.resetState();
  }

  private resetState(): void {
    this.activeFilter.set('all');
    this.searchQuery.set('');
    this.selectedMonthYear.set('all');
    this.monthYearOptions.set([]);
    this.isMonthDropdownOpen.set(false);
    this.configData.set([]);
    this.open = false;
    this.config = null;
  }

  private generateMonthYearOptions(): void {
    const data = this.config?.data || [];
    const dateFieldKey = this.config?.dateFieldKey || 'arrangement_date';
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'];
    
    const monthYearMap = new Map<string, MonthYearOption>();
    
    data.forEach((item: any) => {
      const dateStr = item[dateFieldKey] || item['scheduled_date'];
      if (dateStr) {
        try {
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            const month = date.getMonth();
            const year = date.getFullYear();
            const key = `${year}-${String(month + 1).padStart(2, '0')}`;
            
            if (monthYearMap.has(key)) {
              // Increment count for existing month
              const existing = monthYearMap.get(key)!;
              existing.count++;
            } else {
              // Create new entry with count 1
              monthYearMap.set(key, {
                key,
                label: `${monthNames[month]} ${year}`,
                month: month + 1,
                year,
                count: 1
              });
            }
          }
        } catch {
          // Skip invalid dates
        }
      }
    });
    
    // Sort by year and month (descending - most recent first)
    const sortedOptions = Array.from(monthYearMap.values()).sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.month - a.month;
    });
    
    this.monthYearOptions.set(sortedOptions);
  }

  // Get searchable column keys
  private getSearchableColumns(): string[] {
    if (this.config?.searchableColumns && this.config.searchableColumns.length > 0) {
      return this.config.searchableColumns;
    }
    // Default: search all column keys
    return this.config?.columns?.map(col => col.key) || [];
  }

  // Search filter logic
  private matchesSearch(item: any, query: string): boolean {
    if (!query.trim()) return true;
    
    const searchTerms = query.toLowerCase().trim().split(/\s+/);
    const searchableColumns = this.getSearchableColumns();
    
    // Item matches if ALL search terms are found in ANY of the searchable fields
    return searchTerms.every(term => {
      return searchableColumns.some(key => {
        const value = item[key];
        if (value === null || value === undefined) return false;
        
        // Handle different value types
        const stringValue = String(value).toLowerCase();
        return stringValue.includes(term);
      }) || 
      // Also search in common additional fields
      ['created_by', 'admin_name', 'patient', 'patient_name', 'slot_name', 'slot_label', 'slot'].some(extraKey => {
        const value = item[extraKey];
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(term);
      });
    });
  }

  filteredData = computed(() => {
    let data = this.configData();
    if (!data || data.length === 0) return [];
    
    // Apply month-year filter first
    const monthYearFilter = this.selectedMonthYear();
    if (monthYearFilter !== 'all' && this.config?.enableMonthYearFilter) {
      const dateFieldKey = this.config?.dateFieldKey || 'arrangement_date';
      const [filterYear, filterMonth] = monthYearFilter.split('-').map(Number);
      
      data = data.filter((item: any) => {
        const dateStr = item[dateFieldKey] || item['scheduled_date'];
        if (!dateStr) return false;
        
        try {
          const date = new Date(dateStr);
          if (isNaN(date.getTime())) return false;
          return date.getFullYear() === filterYear && (date.getMonth() + 1) === filterMonth;
        } catch {
          return false;
        }
      });
    }
    
    // Apply search filter
    const query = this.searchQuery();
    if (query.trim()) {
      data = data.filter(item => this.matchesSearch(item, query));
    }
    
    // Then apply category filter
    const filter = this.activeFilter();
    if (filter === 'all') return data;
    
    // Special filter for low amount partial payments
    if (filter === 'low_amount') {
      return data.filter(item => item.is_low_amount === true);
    }

    if (filter === 'normal_amount') {
      return data.filter(item => item.is_normal_amount === true);
    }

    if (filter === 'recurring' || filter === 'partial') {
      return data.filter(item => 
        item.payment_type_key?.toLowerCase() === filter.toLowerCase() ||
        item.payment_type_name?.toLowerCase() === filter.toLowerCase()
      );
    }
    
    // Filter based on category field (for recurring analysis panel)
    const categoryFiltered = data.filter(item => 
      item.category === filter
    );
    if (categoryFiltered.length > 0) return categoryFiltered;
    
    // Filter based on slot_key or similar field
    return data.filter(item => 
      item.slot_key?.toLowerCase() === filter.toLowerCase() ||
      item.slot?.toLowerCase() === filter.toLowerCase()
    );
  });

  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery.set(input.value);
  }

  clearSearch(): void {
    this.searchQuery.set('');
  }

  // accentClasses removed as per user request

  /*
  get accentClasses() {
    const color = this.config?.accentColor || 'indigo';
    const classes = {
      indigo: {
        headerBg: 'from-indigo-600 to-violet-600',
        headerBorder: 'dark:border-indigo-500/30',
        filterActive: 'bg-indigo-500 text-white border-indigo-500 shadow-sm shadow-indigo-500/25',
        filterInactiveLight: 'bg-white dark:bg-slate-800/60 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700/50 hover:border-indigo-400 dark:hover:border-line-strong hover:text-indigo-600 dark:hover:text-white hover:bg-indigo-50 dark:hover:bg-transparent',
      },
      emerald: {
        headerBg: 'from-emerald-600 to-teal-600',
        headerBorder: 'dark:border-emerald-500/30',
        filterActive: 'bg-emerald-500 text-white border-emerald-500 shadow-sm shadow-emerald-500/25',
        filterInactiveLight: 'bg-white dark:bg-slate-800/60 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700/50 hover:border-emerald-400 dark:hover:border-emerald-500/50 hover:text-emerald-600 dark:hover:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-transparent',
      },
      amber: {
        headerBg: 'from-amber-600 to-orange-600',
        headerBorder: 'dark:border-amber-500/30',
        filterActive: 'bg-amber-500 text-white border-amber-500 shadow-sm shadow-amber-500/25',
        filterInactiveLight: 'bg-white dark:bg-slate-800/60 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700/50 hover:border-amber-400 dark:hover:border-amber-500/50 hover:text-amber-600 dark:hover:text-amber-300 hover:bg-amber-50 dark:hover:bg-transparent',
      },
      rose: {
        headerBg: 'from-rose-600 to-pink-600',
        headerBorder: 'dark:border-rose-500/30',
        filterActive: 'bg-rose-500 text-white border-rose-500 shadow-sm shadow-rose-500/25',
        filterInactiveLight: 'bg-white dark:bg-slate-800/60 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700/50 hover:border-rose-400 dark:hover:border-rose-500/50 hover:text-rose-600 dark:hover:text-rose-300 hover:bg-rose-50 dark:hover:bg-transparent',
      },
    };
    return classes[color];
  }
  */

  close() {
    this.activeFilter.set('all');
    this.searchQuery.set('');
    this.selectedMonthYear.set('all');
    this.isMonthDropdownOpen.set(false);
    this.open = false;
    this.closed.emit();
  }

  setFilter(filterKey: string) {
    this.activeFilter.set(filterKey);
  }

  setMonthYearFilter(key: string) {
    this.selectedMonthYear.set(key);
  }

  onMonthYearChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    this.selectedMonthYear.set(select.value);
  }

  toggleMonthDropdown(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.isMonthDropdownOpen.set(!this.isMonthDropdownOpen());
  }

  selectMonthYear(key: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.selectedMonthYear.set(key);
    this.isMonthDropdownOpen.set(false);
  }

  getSelectedMonthLabel(): string {
    const selected = this.selectedMonthYear();
    if (selected === 'all') return 'All Months';
    
    const option = this.monthYearOptions().find(opt => opt.key === selected);
    return option?.label || 'All Months';
  }

  getTotalRecordCount(): number {
    return this.config?.data?.length || 0;
  }

  formatValue(item: any, column: TransactionLogColumn): string {
    const value = item[column.key];
    if (value === null || value === undefined) return '-';

    switch (column.type) {
      case 'currency':
        return typeof value === 'number' 
          ? `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          : value;
      case 'date':
        try {
          const date = new Date(value);
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        } catch {
          return value;
        }
      default:
        return String(value);
    }
  }

  getStatusClasses(status: string): string {
    const statusLower = status?.toLowerCase() || '';
    
    if (statusLower.includes('success') || statusLower.includes('completed') || statusLower.includes('active') || statusLower.includes('paid')) {
      return 'bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30';
    }
    
    if (statusLower.includes('fail') || statusLower.includes('error') || statusLower.includes('declined') || statusLower.includes('overdue') || statusLower.includes('cancelled')) {
      return 'bg-rose-100 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-500/30';
    }
    
    if (statusLower.includes('pending') || statusLower.includes('processing') || statusLower.includes('scheduled')) {
      return 'bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-500/30';
    }

    if (statusLower.includes('recurring')) {
       return 'bg-indigo-100 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 border-indigo-200 dark:border-indigo-500/30';
    }
    
    if (statusLower.includes('partial')) {
       return 'bg-blue-100 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-500/30';
    }

    return 'bg-slate-100 dark:bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-500/30';
  }
}
